import { HttpContext, HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ToastService } from '../services/toast.service';
import { isQuotaOrRateLimitError } from './quota-error.interceptor';

/**
 * Opt-in escape hatch for a request whose component already renders its own 403 message
 * (e.g. an inline "permission denied" banner next to the form). Set on the request via
 * {@link skipGenericForbiddenToast} so {@link globalErrorInterceptor} doesn't pile a second,
 * redundant "you don't have permission" toast on top of that inline message. Mirrors the
 * `isQuotaOrRateLimitError` escape hatch quota-error.interceptor.ts exports, but the other
 * way round: there the *component* checks a helper to skip its own toast; here the
 * *interceptor* checks a per-request flag to skip its own.
 */
export const SKIP_GENERIC_FORBIDDEN_TOAST = new HttpContextToken<boolean>(() => false);

/** Attach to an HttpClient request's `context` to suppress the generic 403 toast for it. */
export function skipGenericForbiddenToast(): HttpContext {
    return new HttpContext().set(SKIP_GENERIC_FORBIDDEN_TOAST, true);
}

/**
 * Opt-in escape hatch for a request that retries transient failures itself (e.g. an RxJS
 * `retry()`), where each attempt re-runs the full interceptor chain. Without this, a 5xx that
 * recovers on the second or third attempt still toasts once per failed attempt before quietly
 * succeeding — worse UX than not retrying at all. Set via {@link skipGenericServerErrorToast}
 * so {@link globalErrorInterceptor} stays silent on 5xx for every attempt of that request; the
 * caller's own `error:` handler (and its `error`/`loading` signals, if any) remains the source
 * of truth for a terminal failure.
 */
export const SKIP_GENERIC_SERVER_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/** Attach to an HttpClient request's `context` to suppress the generic 5xx toast for it. */
export function skipGenericServerErrorToast(): HttpContext {
    return new HttpContext().set(SKIP_GENERIC_SERVER_ERROR_TOAST, true);
}

/**
 * Last-resort net for any 5xx or unrecognised 403 that no more specific handler already
 * turned into calm, actionable UX. Registered *after* {@link quotaErrorInterceptor} in
 * `app.config.ts` so it never double-toasts a quota/rate-limit response that interceptor
 * already handled (checked via {@link isQuotaOrRateLimitError}).
 *
 * - **Any 5xx** → "Something went wrong on our side — it's been logged." Today core-api has
 *   no correlation/request-id convention (no global exception filter, no `x-request-id`-style
 *   header — verified by grep), so there is nothing to append; if/when one exists, read it
 *   here rather than fabricating one.
 * - **403 without a recognised `QUOTA_EXCEEDED`/`QUOTA_EXCEEDED_STORAGE` body** → "You don't
 *   have permission for that action." Skipped when the request opted out via
 *   {@link skipGenericForbiddenToast} (used by the two flows — API key create, workspace
 *   create — that already render their own specific inline "permission denied" message).
 *
 * Always re-throws so component-level `error:` handlers still run (clearing spinners, etc.).
 * This never swallows — it only ever adds a toast on top.
 */
export const globalErrorInterceptor: HttpInterceptorFn = (req, next) => {
    const toast = inject(ToastService);

    return next(req).pipe(
        catchError((error: unknown) => {
            if (error instanceof HttpErrorResponse) {
                if (isQuotaOrRateLimitError(error)) {
                    // Already surfaced (upgrade modal / transient-quota or slow-down toast).
                } else if (error.status >= 500 && !req.context.get(SKIP_GENERIC_SERVER_ERROR_TOAST)) {
                    toast.error("Something went wrong on our side — it's been logged.");
                } else if (error.status === 403 && !req.context.get(SKIP_GENERIC_FORBIDDEN_TOAST)) {
                    toast.error("You don't have permission for that action.");
                }
            }
            return throwError(() => error);
        })
    );
};
